import type { ActionReducerMapBuilder } from "@reduxjs/toolkit";
import {
  addStorageObject,
  attachChildToParent,
  createSharedObject,
  deleteSharedObject,
  detachChildFromParent,
  editStorageObject,
  fetchAllStorageObjects,
  fetchAllStorageObjectsSharing,
  fetchStorageObjectById,
  removeStorageObject,
  updateSharedObject,
} from "./actions/actions";
import {
  createStorageObjectVersionAction,
  fetchAllStorageObjectVersions,
  updateStorageObjectVersionAction,
} from "./actions/versioning-actions";
import { storageObjectAdapter } from "./adapter";
import type { StorageObjectState } from "./types";
import { getLatestDateVersionValue } from "./utils/getLatestDateVersionValue";
import { normalizeStorageObjects } from "./utils/normalizeStorageObjects/normalizeStorageObjects";
import {
  StorageObjectValue,
  StorageScope,
} from "../../../services/api/instances/storageApi/types/service_types";
import { NormalizedStorageObject } from "../../../services/api/instances/storageApi/types/object_types";

export function fetchAllStorageObjectsReducerBuilder(
  builder: ActionReducerMapBuilder<StorageObjectState>,
) {
  builder.addCase(fetchAllStorageObjects.pending, (state, action) => {
    const scope = action.meta.arg.service as StorageScope;
    state[scope].loading = true;
    state[scope].error = null;
  });
  builder.addCase(fetchAllStorageObjects.fulfilled, (state, action) => {
    const scope = action.meta.arg.service as StorageScope;

    const normalized = normalizeStorageObjects(action.payload);

    storageObjectAdapter.setAll(state[scope].data, normalized.entities);
    state[scope].hasMutation = false;
    state[scope].loading = false;
  });
  builder.addCase(fetchAllStorageObjects.rejected, (state, action) => {
    const scope = action.meta.arg.service as StorageScope;

    if (action.error.name === "ConditionError") {
      state[scope].loading = false;
      return;
    }
    state[scope].loading = false;
    state[scope].error = action.error.message || "Ошибка загрузки объектов";
  });
}

export function fetchStorageObjectByIdReducerBuilder(
  builder: ActionReducerMapBuilder<StorageObjectState>,
) {
  builder.addCase(fetchStorageObjectById.pending, (state, action) => {
    const scope = action.meta.arg.service as StorageScope;
    state[scope].loading = true;
    state[scope].error = null;
  });
  builder.addCase(fetchStorageObjectById.fulfilled, (state, action) => {
    const { service, id } = action.meta.arg;

    const normalized = state[service].data.entities;
    const target = normalized[id];
    const hasChanged =
      !target || target.object_date !== action.payload.object_date;

    if (hasChanged) {
      storageObjectAdapter.setOne(state[service].data, {
        ...target,
        friendly_name: action.payload.friendly_name,
        value: action.payload.value,
        object_date: action.payload.object_date,
      });
    }

    state[service].loading = false;
  });
  builder.addCase(fetchStorageObjectById.rejected, (state, action) => {
    const scope = action.meta.arg.service as StorageScope;
    state[scope].loading = false;
    state[scope].error = action.error.message || "Ошибка загрузки объектов";
  });
}

export function addStorageObjectsReducerBuilder(
  builder: ActionReducerMapBuilder<StorageObjectState>,
) {
  builder.addCase(addStorageObject.fulfilled, (state, action) => {
    const scope = action.meta.arg.service as StorageScope;
    state[scope].hasMutation = true;
    if (!action.payload) return;
    const normalized = action.payload;
    storageObjectAdapter.addOne(state[scope].data, normalized);
  });
}

export function updateStorageObjectsReducerBuilder(
  builder: ActionReducerMapBuilder<StorageObjectState>,
) {
  builder.addCase(editStorageObject.pending, (state, action) => {
    const scope = action.meta.arg.service as StorageScope;
    state[scope].loading = true;
  });
  builder.addCase(editStorageObject.fulfilled, (state, action) => {
    const scope = action.meta.arg.service as StorageScope;
    if (!action.payload) return;
    const { owner, ...updatedObject } = action.payload;
    state[scope].loading = false;
    const existingObject = state[scope].data.entities[updatedObject.id];

    if (existingObject) {
      const mergedObject = {
        ...updatedObject,
        parentId: existingObject.parentId,
        childrenIds: existingObject.childrenIds,
      };
      storageObjectAdapter.upsertOne(state[scope].data, mergedObject);
    } else {
      storageObjectAdapter.upsertOne(state[scope].data, updatedObject);
    }
    state[scope].hasMutation = true;
  });
  builder.addCase(editStorageObject.rejected, (state, action) => {
    const scope = action.meta.arg.service as StorageScope;
    state[scope].loading = false;
    state[scope].error = action.error.message || "Ошибка обновления объектов";
  });
}

export function deleteStorageObjectsReducerBuilder(
  builder: ActionReducerMapBuilder<StorageObjectState>,
) {
  builder.addCase(removeStorageObject.pending, (state, action) => {
    const scope = action.meta.arg.service as StorageScope;
    state[scope].loading = true;
  });
  builder.addCase(removeStorageObject.fulfilled, (state, action) => {
    const scope = action.meta.arg.service as StorageScope;
    storageObjectAdapter.removeOne(state[scope].data, action.payload.id);
    state[scope].loading = false;
    state[scope].hasMutation = true;
  });
  builder.addCase(removeStorageObject.rejected, (state, action) => {
    const scope = action.meta.arg.service as StorageScope;
    state[scope].loading = false;
    state[scope].error = action.error.message || "Ошибка удаления объектов";
  });
}

export function addChildToStorageObjectsReducerBuilder(
  builder: ActionReducerMapBuilder<StorageObjectState>,
) {
  builder.addCase(attachChildToParent.pending, (state, action) => {
    const scope = action.meta.arg.service as StorageScope;
    state[scope].loading = true;
  });
  builder.addCase(attachChildToParent.fulfilled, (state, action) => {
    const scope = action.meta.arg.service as StorageScope;
    const parentId = action.meta.arg.parentId;
    const childIds = action.meta.arg.childIds;

    const parent = state[scope].data.entities[parentId];

    childIds.forEach((childId) => {
      const child = state[scope].data.entities[childId];

      if (child?.parentId && child.parentId !== parentId) {
        const prevParent = state[scope].data.entities[child.parentId];
        if (prevParent) {
          prevParent.childrenIds = prevParent.childrenIds.filter(
            (id) => id !== childId,
          );
        }
      }

      if (parent && !parent.childrenIds.includes(childId)) {
        parent.childrenIds = [...parent.childrenIds, childId];
      }

      if (child) {
        child.parentId = parentId;
      }
    });

    state[scope].loading = false;
  });
  builder.addCase(attachChildToParent.rejected, (state, action) => {
    const scope = action.meta.arg.service as StorageScope;
    state[scope].loading = false;
    state[scope].error =
      action.error.message || "Ошибка добавления вложенного объекта";
  });
}

export function removeChildFromStorageObjectsReducerBuilder(
  builder: ActionReducerMapBuilder<StorageObjectState>,
) {
  builder.addCase(detachChildFromParent.pending, (state, action) => {
    const scope = action.meta.arg.service as StorageScope;
    state[scope].loading = true;
  });

  builder.addCase(detachChildFromParent.fulfilled, (state, action) => {
    const { parentId, childIds, service } = action.payload;
    const scope = service as StorageScope;

    if (!state[scope]) return;

    const parent = state[scope].data.entities[parentId];
    if (parent && Array.isArray(parent.childrenIds)) {
      storageObjectAdapter.updateOne(state[scope].data, {
        id: parentId,
        changes: {
          childrenIds: parent.childrenIds.filter(
            (id) => !childIds.includes(id),
          ),
        },
      });
    }
    storageObjectAdapter.updateMany(
      state[scope].data,
      childIds
        .filter((childId) => state[scope].data.entities[childId])
        .map((childId) => ({
          id: childId,
          changes: {
            parentId: null,
          },
        })),
    );

    state[scope].loading = false;
  });

  builder.addCase(detachChildFromParent.rejected, (state, action) => {
    const scope = action.meta.arg.service as StorageScope;
    state[scope].loading = false;
    state[scope].error =
      action.error.message || "Ошибка удаления вложенного объекта";
  });
}

export function fetchAllStorageObjectVersionsReducerBuilder(
  builder: ActionReducerMapBuilder<StorageObjectState>,
) {
  builder.addCase(fetchAllStorageObjectVersions.fulfilled, (state, action) => {
    const scope = action.meta.arg.service as StorageScope;
    state[scope].versions = action.payload;
    const id = state[scope].selectedId ?? "";
    const versions = action.payload[id];
    if (versions?.length !== 0) {
      const latestVersion = getLatestDateVersionValue(versions);
      state[scope].selectedVersionId = latestVersion?.version_id as string;
    }
  });
}

export function createStorageObjectVersionReducerBuilder(
  builder: ActionReducerMapBuilder<StorageObjectState>,
) {
  builder.addCase(
    createStorageObjectVersionAction.fulfilled,
    (state, action) => {
      const scope = action.meta.arg.service as StorageScope;
      state[scope].versions = action.payload;
    },
  );
}

export function updateStorageObjectVersionReducerBuilder(
  builder: ActionReducerMapBuilder<StorageObjectState>,
) {
  builder.addCase(
    updateStorageObjectVersionAction.fulfilled,
    (state, action) => {
      const scope = action.meta.arg.service as StorageScope;

      if (!action.payload) {
        return;
      }

      state[scope].versions = action.payload;
    },
  );
}

export const fetchAllStorageObjectsSharingReducerBuilder = (
  builder: ActionReducerMapBuilder<StorageObjectState>,
) => {
  builder.addCase(fetchAllStorageObjectsSharing.fulfilled, (state, action) => {
    state.sharedObjects = action.payload;
  });
};

export const createStorageObjectSharingReducerBuilder = (
  builder: ActionReducerMapBuilder<StorageObjectState>,
) => {
  builder.addCase(createSharedObject.fulfilled, (state, action) => {
    console.log("Created shared object:", action.payload);
    state.sharedObjects = [...state.sharedObjects, ...action.payload];
  });
};

export const updateStorageObjectSharingReducerBuilder = (
  builder: ActionReducerMapBuilder<StorageObjectState>,
) => {
  builder.addCase(updateSharedObject.fulfilled, (state, action) => {
    state.sharedObjects = state.sharedObjects.map((obj) => {
      const updated = action.payload.find((p) => p.id === obj.id);
      return updated ?? obj;
    });
  });
};

export const deleteStorageObjectSharingReducerBuilder = (
  builder: ActionReducerMapBuilder<StorageObjectState>,
) => {
  builder.addCase(deleteSharedObject.fulfilled, (state, action) => {
    const { successfulIds } = action.payload;
    state.sharedObjects = state.sharedObjects.filter(
      (obj) => !successfulIds.includes(obj.id),
    );
  });
};
